import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { ArrowLeft } from "lucide-react";
import { useLang } from "@/hooks/use-language";
import { useSmartBack } from "@/hooks/use-smart-back";
import {
  QUIET_COPY,
  QUIET_ACTIVITIES,
  type QuietActivity,
} from "@/lib/quiet-corner-copy";
import { BreathingCircle } from "@/components/quiet/BreathingCircle";
import { EmotionCards } from "@/components/quiet/EmotionCards";
import { FreeDrawing } from "@/components/quiet/FreeDrawing";
import { Reflection } from "@/components/quiet/Reflection";
import { BedtimeCalm } from "@/components/quiet/BedtimeCalm";

export const Route = createFileRoute("/_authenticated/app/child/$childId/quiet")({
  component: QuietCorner,
});

function QuietCorner() {
  const { childId } = Route.useParams();
  const navigate = useNavigate();
  const goBack = useSmartBack(() =>
    navigate({ to: "/app/child/$childId", params: { childId } }),
  );
  const { lang } = useLang();
  const isAr = lang === "ar";
  const [active, setActive] = useState<QuietActivity | null>(null);

  const activity = active ? QUIET_ACTIVITIES.find((a) => a.id === active)! : null;

  return (
    <div className="px-5 py-6 max-w-2xl mx-auto min-h-screen bg-gradient-to-b from-background to-primary/[0.03]">
      <button
        onClick={() => (active ? setActive(null) : goBack())}
        className="flex items-center gap-2 text-sm font-medium text-foreground/60 hover:text-foreground mb-6"
      >
        <ArrowLeft className="size-4" />
        {isAr ? QUIET_COPY.back.ar : QUIET_COPY.back.en}
      </button>

      {!active && (
        <>
          <div className="text-center mb-10">
            <div className="text-5xl mb-4">🤍</div>
            <h1
              className={`${isAr ? "font-display-ar" : "font-display-en"} text-3xl text-primary mb-2`}
              dir={isAr ? "rtl" : "ltr"}
            >
              {isAr ? QUIET_COPY.title.ar : QUIET_COPY.title.en}
            </h1>
            <p
              className="text-sm text-foreground/65 max-w-sm mx-auto"
              dir={isAr ? "rtl" : "ltr"}
            >
              {isAr ? QUIET_COPY.subtitle.ar : QUIET_COPY.subtitle.en}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {QUIET_ACTIVITIES.map((a) => (
              <button
                key={a.id}
                onClick={() => setActive(a.id)}
                className="text-start bg-card border border-clay/30 rounded-3xl p-5 hover:border-primary/50 hover:-translate-y-0.5 transition-all"
              >
                <div className="text-3xl mb-3">{a.emoji}</div>
                <p
                  className={`${isAr ? "font-display-ar" : "font-display-en"} text-lg text-primary mb-1`}
                  dir={isAr ? "rtl" : "ltr"}
                >
                  {isAr ? a.title_ar : a.title_en}
                </p>
                <p
                  className="text-xs text-foreground/55"
                  dir={isAr ? "rtl" : "ltr"}
                >
                  {isAr ? a.hint_ar : a.hint_en}
                </p>
              </button>
            ))}
          </div>
        </>
      )}

      {active && activity && (
        <div>
          <div className="text-center mb-2">
            <div className="text-3xl mb-2">{activity.emoji}</div>
            <h2
              className={`${isAr ? "font-display-ar" : "font-display-en"} text-2xl text-primary`}
              dir={isAr ? "rtl" : "ltr"}
            >
              {isAr ? activity.title_ar : activity.title_en}
            </h2>
          </div>
          {active === "breathing"  && <BreathingCircle isAr={isAr} />}
          {active === "emotions"   && <EmotionCards isAr={isAr} />}
          {active === "drawing"    && <FreeDrawing isAr={isAr} />}
          {active === "reflection" && <Reflection isAr={isAr} />}
          {active === "bedtime"    && <BedtimeCalm isAr={isAr} />}
        </div>
      )}
    </div>
  );
}
